import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { bindActiveAccount, devBypass, msalInstance } from './auth/msal';
import { AppLayout } from './components/AppLayout';
import { useCurrentUser } from './hooks/useCurrentUser';
import { AccountingPage } from './pages/AccountingPage';
import { AuditPage } from './pages/AuditPage';
import { CatalogPage } from './pages/CatalogPage';
import { ChannelsPage } from './pages/ChannelsPage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/customers/CustomerDetailPage';
import { DashboardPage } from './pages/DashboardPage';
import { DevicesPage } from './pages/DevicesPage';
import { FiscalPage } from './pages/FiscalPage';
import { FloorPage } from './pages/FloorPage';
import { InventoryPage } from './pages/InventoryPage';
import { StockItemDetailPage } from './pages/inventory/StockItemDetailPage';
import { McpPage } from './pages/McpPage';
import { PayrollPage } from './pages/PayrollPage';
import { PricingPage } from './pages/PricingPage';
import { PayrollRunPage } from './pages/payroll/PayrollRunPage';
import { ReferralsPage } from './pages/ReferralsPage';
import { RegisterPage } from './pages/RegisterPage';
import { ReportsPage } from './pages/ReportsPage';
import { PartnerDetailPage } from './pages/referrals/PartnerDetailPage';
import { DrawerSessionPage } from './pages/register/DrawerSessionPage';
import { SettingsPage } from './pages/SettingsPage';
import { SignInPage } from './pages/SignInPage';
import { StaffPage } from './pages/StaffPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { RoleDetailPage } from './pages/staff/RoleDetailPage';
import { StaffDetailPage } from './pages/staff/StaffDetailPage';
import { SupplierDetailPage } from './pages/suppliers/SupplierDetailPage';
import { TaxPage } from './pages/TaxPage';

/**
 * A screen only the owner may open, for somebody who typed the address rather than followed a link.
 *
 * It sends them to the dashboard rather than showing a refusal, because there is nothing for them
 * to do about it and a page saying so would be a page whose whole content is "no". The API refuses
 * the same person at the mount regardless — this exists so the screen does not render a page of
 * broken tiles on the way to being told.
 *
 * Nothing is decided while the identity is still loading: a redirect fired on an unanswered `me`
 * would bounce the owner off his own reports on every cold load.
 */
function OwnerOnly({ children }: { children: ReactElement }) {
  const { isOwner, isLoading } = useCurrentUser();
  if (isLoading) return null;
  return isOwner ? children : <Navigate to="/" replace />;
}

function Shell() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="tax" element={<TaxPage />} />
        <Route path="staff" element={<StaffPage />} />
        {/* Before the person route only by reading order; the two never collide, since a role
            path is two segments deep and a person's is one. */}
        <Route path="staff/roles/:roleId" element={<RoleDetailPage />} />
        <Route path="staff/:staffId" element={<StaffDetailPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:customerId" element={<CustomerDetailPage />} />
        <Route path="floor" element={<FloorPage />} />
        <Route path="fiscal" element={<FiscalPage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="inventory/:itemId" element={<StockItemDetailPage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="suppliers/:supplierId" element={<SupplierDetailPage />} />
        <Route path="channels" element={<ChannelsPage />} />
        <Route path="referrals" element={<ReferralsPage />} />
        {/* "partners" in the path rather than the bare id: a partner is one of several things this
            screen lists, and /referrals/:id leaves nowhere to put the next one. */}
        <Route path="referrals/partners/:profileId" element={<PartnerDetailPage />} />
        <Route path="register" element={<RegisterPage />} />
        {/* "drawers" in the path rather than the bare id: a drawer is one of several things the
            register screen lists, and /register/:id leaves nowhere to put the next one. */}
        <Route path="register/drawers/:drawerSessionId" element={<DrawerSessionPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="payroll/runs/:runId" element={<PayrollRunPage />} />
        <Route
          path="reports"
          element={
            <OwnerOnly>
              <ReportsPage />
            </OwnerOnly>
          }
        />
        <Route path="filed-months" element={<AccountingPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="mcp" element={<McpPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

bindActiveAccount();

export function App() {
  if (devBypass) return <Shell />;
  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <Shell />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInPage />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}
